import * as React from 'react';
import { Globe, LifeBuoy, LogOut, Settings } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { systemClock } from '@reos/config';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { SupportWidget, useSupportReporting } from './SupportWidget.js';
import { AppearanceMenuSection } from './appearance.js';
import { GuideMenuRow } from './guide-toggle.js';
import { GuideHighlight } from './guide-highlight.js';
import { DemoBanner } from './demo-banner.js';
import { TrialBanner } from './trial-banner.js';
import { initials } from './avatar.js';
import { AdminShell, type AdminShellNavGroup, type AdminShellNavItem } from './admin-shell.js';
import { ProductLogo } from './product-logo.js';
import { TenantSwitch } from './tenant-switch.js';
import { CommandPalette } from './command-palette.js';
import { isNavItemVisible } from '../lib/capabilities.js';
import {
  MENU_GROUP_LABELS,
  MENU_GROUP_ORDER,
  MOBILE_BAR_IDS,
  SHELL_MENU_ITEMS,
  NEW_CASE_ITEM,
  isFocusModeRoute,
  isNavItemActive,
  isNewCaseActivePath,
  navItemById,
  type Blok1NavItem,
} from '../lib/nav.js';
import { listContracts } from '../lib/contracts-api.js';
import { listInvoices } from '../lib/invoices-api.js';
import {
  listUnreadNotifications,
  UNREAD_NOTIFICATIONS_QUERY_KEY,
} from '../lib/broker-notifications-api.js';
import { NOVINKY_ON_DNESEK } from './dnesek-novinky.js';
import { listTodayTasks } from '../lib/tasks-api.js';
import { overdueTaskCount } from '../lib/dnesek-model.js';
import {
  ALL_CONTRACTS_QUERY_KEY,
  ISSUED_INVOICES_QUERY_KEY,
  SIGNATURE_SESSIONS_QUERY_KEY,
  countOverdueInvoices,
  todayTasksQueryKey,
} from '../lib/waiting-for-me.js';
import { onYouContractCountIfKnown } from '../lib/contract-standing.js';
import { listSignatureSessions } from '../lib/signature-api.js';
import { startClientErrorReporting, stopClientErrorReporting } from '../lib/report-collector.js';
import { resetSession, useSession } from '../stores/session.js';
import { signOut } from '../lib/api.js';
import { SUPPORT_TEXTS } from '../lib/texts.js';
import { LocaleProvider, useT, useLocale, useSetLocale, SUPPORTED_UI_LOCALES } from '../lib/i18n.js';

/** Dev/demo prepinac jazyka — viz ROZHODNUTI u `deriveLocaleFromSession` v lib/i18n.ts. */
function LocaleSwitch(): React.JSX.Element {
  const locale = useLocale();
  const setLocale = useSetLocale();
  return (
    <select
      aria-label="cs / en"
      value={locale}
      onChange={(event) => setLocale(event.target.value as (typeof SUPPORTED_UI_LOCALES)[number])}
      className="text-sm"
      data-testid="locale-switch"
    >
      {SUPPORTED_UI_LOCALES.map((code) => (
        <option key={code} value={code}>
          {code}
        </option>
      ))}
    </select>
  );
}

/**
 * Krok UXN1 (R.B. 9. 10. 2026: ANO) — AVATAR MENU JEN PRO UCET. Nalez auditu
 * GPT: pracovni moduly (Úkoly, Smlouvy, Nemovitosti…) byly schovane tady,
 * v dlouhem posuvnem seznamu mezi jazykem a vzhledem. Od tohoto kroku maji
 * vlastni polozku v levem menu (`lib/nav.ts`, skupiny Obchod/Práce/Peníze/
 * Přehledy/Kancelář) a avatar nese jen: jmeno/e-mail, organizaci (TenantSwitch),
 * jazyk, velikost pisma, vzhled, Nastaveni a odhlaseni.
 *
 * Krok UXR1 — spoustecem uz neni tlacitko s e-mailem, ale kolecko s
 * inicialami podle mockupu. E-mail se z pristupnosti NESMI ztratit
 * (bezpecnostni ustava kitu: aktivni kontext musi byt videt), proto je
 * v hlavicce menu a `data-testid="user-email"` zustava na nem.
 */
function AvatarMenu({
  name,
  email,
  onLogout,
  onReportProblem,
}: {
  readonly name: string;
  readonly email: string;
  readonly onLogout: () => void;
  /** `undefined` = hlaseni problemu neni zapojene, polozka se nevykresli (MOB1). */
  readonly onReportProblem: (() => void) | undefined;
}): React.JSX.Element {
  const t = useT();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="rh-a2-avatar"
          data-testid="avatar-menu-trigger"
          aria-label={t('webAdmin.layout.avatarMenuLabel')}
        >
          {initials(name, email)}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className="rh-a2-menu__pop" data-testid="avatar-menu">
          <div className="rh-a2-menu__head">
            <div style={{ fontWeight: 600 }}>{name}</div>
            <div className="rh-a2-meta" data-testid="user-email">
              {email}
            </div>
          </div>
          {/* Krok UXD1 bod 11 (R.B. 12. 10. 22:22): „Odhlásit" hned pod
              jméno/účet, ne az pod Vzhled/Nastavení. */}
          <DropdownMenu.Item asChild>
            <button type="button" className="rh-a2-menu__row" onClick={onLogout} data-testid="logout-button">
              <span className="rh-a2-ico" aria-hidden="true">
                <LogOut size={18} strokeWidth={1.75} />
              </span>
              {t('webAdmin.layout.logout')}
            </button>
          </DropdownMenu.Item>
          <div className="rh-a2-menu__sep" />
          <div className="rh-a2-menu__row">
            <TenantSwitch />
          </div>
          <div className="rh-a2-menu__row">
            <span className="rh-a2-ico" aria-hidden="true">
              <Globe size={18} strokeWidth={1.75} />
            </span>
            <span>{t('webAdmin.layout.languageLabel')}</span>
            <span style={{ marginLeft: 'auto' }}>
              <LocaleSwitch />
            </span>
          </div>
          {/*
            Krok UXV4p bod 3 — „Velikost písma" a „Vzhled" PRIMO V MENU
            PROFILU, aby byly na jedno klepnuti z kazde obrazovky (R.B. 4. 10.
            08:53: „nevidim tam ty velikosti textu" — zily jen v /nastaveni).
            Tataz komponenta je i v Nastaveni → Vzhled.
          */}
          <div className="rh-a2-menu__sep" />
          <AppearanceMenuSection />
          <div className="rh-a2-menu__sep" />
          <DropdownMenu.Item asChild>
            <Link to="/nastaveni" className="rh-a2-menu__row" data-testid="avatar-menu-settings">
              <span className="rh-a2-ico" aria-hidden="true">
                <Settings size={18} strokeWidth={1.75} />
              </span>
              {t('webAdmin.layout.settingsLink')}
            </Link>
          </DropdownMenu.Item>
          {/* Krok PRU1 bod 3: pruvodce „Co si vyzkoušet" skryt / vratit. */}
          <GuideMenuRow className="rh-a2-menu__row" />
          {onReportProblem !== undefined ? (
            <DropdownMenu.Item asChild>
              <button
                type="button"
                className="rh-a2-menu__row"
                data-testid="avatar-menu-report-problem"
                onClick={onReportProblem}
              >
                <span className="rh-a2-ico" aria-hidden="true">
                  <LifeBuoy size={18} strokeWidth={1.75} />
                </span>
                {SUPPORT_TEXTS.openButton}
              </button>
            </DropdownMenu.Item>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Shell aplikace = `AdminShell` (mockup UXM1); web-admin dodava menu, session a sloty. */
/**
 * Krok CRM4: veřejné stránky pro klienta (rezervace prohlídky odkazem) se
 * kreslí BEZ administrace — klient nemá účet, menu kanceláře ani zvonek mu
 * nepatří a shell by se pokoušel načíst session.
 */
export function isPublicClientPath(pathname: string): boolean {
  return pathname.startsWith('/prohlidka/');
}

/**
 * Zivy pruchod 13. 10. (V9): `/login` (i po odhlaseni) vykresloval celou
 * postranni navigaci kanceláře BEZ platne session — nic neprozrazuje, ale
 * prihlasovaci obrazovka vypadala rozbite. Tyto stranky patri NEPRIHLASENE
 * osobe, shell (menu, zvonek, avatar) sem nepatri.
 */
export function isAuthPagePath(pathname: string): boolean {
  return pathname === '/login' || pathname === '/reset-password' || pathname === '/reset-password/confirm';
}

export function Layout(): React.JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const noShell = isPublicClientPath(pathname) || isAuthPagePath(pathname);
  return <LocaleProvider>{noShell ? <Outlet /> : <LayoutShell />}</LocaleProvider>;
}

function LayoutShell(): React.JSX.Element {
  const t = useT();
  const user = useSession((s) => s.user);
  const tenant = useSession((s) => s.tenant);
  const roles = user?.roles ?? [];
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [supportOpen, setSupportOpen] = React.useState(false);
  /*
   * Krok MOB1 bod 3 — „Nahlásit problém" se ukazuje JEN kdyz ho server umi
   * odeslat (`PODPORA_API_URL` + `PODPORA_TOKEN_REOS`). Podminka se cte z
   * JEDNOHO mista spolu se samotnym widgetem, aby nemohla vzniknout polozka
   * v menu, ktera otevre panel, jenz skonci chybou.
   */
  const supportReporting = useSupportReporting();
  const openSupport = supportReporting ? () => { setSupportOpen(true); } : undefined;

  // Session uz nacetl router guard (`ensureSession` v router.tsx) — shell ji
  // jen cte. Jedno volani `GET /api/me` na start aplikace, ne dve.

  /*
   * Krok CER1 bod 1 — chyby v prohlizeci makleře se zapisuji K NAM
   * (`POST /v1/client-errors`), ne az kdyz nekdo napise „něco nešlo".
   *
   * Zapina se VZDY po prihlaseni, ne jen kdyz je dostupne „Nahlásit problém"
   * (do CER1 sber bezel jen za `supportReporting`, takze na instanci bez
   * podpory jsme nevideli nic). Podminka je prihlaseny uzivatel: bez session
   * by hlaseni skoncilo 401 a nemelo by komu patrit.
   */
  React.useEffect(() => {
    if (user === null) return;
    startClientErrorReporting();
  }, [user]);

  const handleLogout = React.useCallback(async () => {
    try {
      await signOut();
    } finally {
      // Po odhlaseni uz nic neposilame (CER1 bod 2).
      stopClientErrorReporting();
      resetSession();
      void navigate({ to: '/login' });
    }
  }, [navigate]);

  /**
   * Krok UXR1 — pocty u polozek menu a u zvonku (mockup: Smlouvy 2,
   * Fakturace 1, zvonek 3). Nejsou to vymyslena cisla: jsou to presne ty
   * polozky, ktere Dnesek ukazuje v „Čeká na mě" (smlouva ceka na podpis,
   * faktura je po splatnosti), a zvonek je jejich soucet. Stejne klice jako
   * na Dnesku, takze shell ani Dnesek nestahuji data dvakrat a cisla si
   * nemohou odporovat. Kosmetika: kdyz volani selze, pocet se neukaze.
   */
  /**
   * Krok SML1 bod 1 — ODZNAK „Smlouvy" je pocet smluv „Na tobě", a pocita ho
   * TATAZ funkce, jakou ma prehled `/smlouvy` u svych pilulek
   * (`lib/contract-standing.ts`).
   *
   * Do tohoto kroku to byl pocet smluv ve stavu `awaiting_signature` — tedy
   * presne tech, ktere cekaji na KLIENTA. Menu tak hlasilo „na tobě" u
   * smlouvy, se kterou makler nemohl nic udelat, a rozpracovany koncept
   * (ktery na nem skutecne lezi) nepocitalo vubec.
   */
  const allContracts = useQuery({
    queryKey: ALL_CONTRACTS_QUERY_KEY,
    queryFn: () => listContracts({}),
    enabled: user !== null,
  });
  const signatureSessions = useQuery({
    queryKey: SIGNATURE_SESSIONS_QUERY_KEY,
    queryFn: () => listSignatureSessions({}),
    enabled: user !== null,
  });
  const issuedInvoices = useQuery({
    queryKey: ISSUED_INVOICES_QUERY_KEY,
    queryFn: () => listInvoices({ status: 'issued' }),
    enabled: user !== null,
  });
  /**
   * Krok TAB1 bod 1 — ODZNAK U „Úkoly": kolik je PO TERMÍNU.
   *
   * Zadání: „stejná funkce jako Dnešek → Na tobě, ne vlastní počet." Proto
   * tentýž endpoint (`/v1/crm/tasks/today`), tentýž klíč react-query
   * (`todayTasksQueryKey` ve `waiting-for-me.ts` — data se tedy stahují
   * jednou pro menu i pro Dnešek) a tentýž výpočet (`overdueTaskCount`).
   * Kosmetika: když volání selže, odznak se prostě neukáže.
   */
  const todayTasks = useQuery({
    queryKey: todayTasksQueryKey(user?.id ?? null),
    queryFn: () => listTodayTasks({ ...(user !== null ? { ownerUserId: user.id } : {}) }),
    enabled: user !== null,
  });

  /*
   * Krok MRGP1 — ODZNAK CEKA NA OBA DOTAZY. „U koho smlouva je" rozhoduji
   * SEZENI PODPISU; dokud nedojdou, spocita se smlouva u klienta jako „na
   * vás" a odznak chvili ukazuje vic, nez prehled `/smlouvy`. Presne o tomhle
   * byl pad sady S16 na 390 px (5. 10. 2026) — dve cisla z teze funkce, ale
   * z JINAK DALEKO nactenych dat. `undefined` = jeste nevime, odznak se
   * nekresli (tytez pravidlo jako pri selhani volani).
   */
  const contractsCount =
    onYouContractCountIfKnown({
      rows: allContracts.data,
      sessions: signatureSessions.data,
    }) ?? undefined;
  /*
   * Krok NOTIF1 bod 3 — POČÍTADLO UPOZORNĚNÍ V MENU. Tentýž dotaz i tentýž
   * klíč react-query jako „Novinky" na Dnešku (`UNREAD_NOTIFICATIONS_QUERY_KEY`),
   * takže data se stahují jednou a kliknutí v Novinkách počítadlo sníží samo.
   * Když volání selže, odznak se prostě neukáže (kosmetika, stejně jako
   * odznak úkolů).
   */
  const unreadNotifications = useQuery({
    queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY,
    queryFn: () => listUnreadNotifications(NOVINKY_ON_DNESEK),
    enabled: user !== null,
  });
  const overdueCount = countOverdueInvoices(issuedInvoices.data ?? [], systemClock.now());
  const overdueTasks = overdueTaskCount(todayTasks.data ?? []);
  const countsByItemId: Readonly<Record<string, number | undefined>> = {
    smlouvy: contractsCount,
    fakturace: overdueCount,
    ukoly: overdueTasks,
  };

  /*
   * Krok UXN1 — LEVE MENU = VSECHNY PRACOVNI MODULY, seskupene (Obchod ·
   * Práce · Peníze · Přehledy · Kancelář). Role-gating (`action`,
   * `ownerAdminOnly`) resi dal `isNavItemVisible` — polozka, kterou role
   * nevidi, v menu proste neni (zadny vstupni bod „v profilu" uz nema smysl,
   * avatar menu zustava jen pro ucet).
   */
  /*
   * Krok QAFIX3 (nalez N06): `SHELL_MENU_ITEMS` = skupiny UXN1 + vstupy podle
   * opravneni („Objednávky reportů" pro `commerce.order.create`). Tentyz
   * seznam cte Ctrl+K, takze panel a paleta nemuzou rict dve ruzne veci.
   */
  const visibleMenuItems = SHELL_MENU_ITEMS.filter((item) => isNavItemVisible(roles, item));
  /*
   * Krok UXP4c (P3, nalez „na Nové zakázce je zvýrazněné Zakázky místo
   * Nová"): `/zakazky/novy` je podcesta `/zakazky`, takze by `isNavItemActive`
   * oznacilo OBOJI. „Nová zakázka" ma svou vlastni dlazdici/polozku, proto
   * na teto strance „Zakázky" aktivni NENI.
   */
  const isNewCasePath = isNewCaseActivePath(pathname);
  const toShellItem = (item: Blok1NavItem): AdminShellNavItem => {
    const count = countsByItemId[item.id];
    return {
      id: item.id,
      label: item.label,
      href: item.path,
      active: isNavItemActive(pathname, item) && !(item.id === 'zakazky' && isNewCasePath),
      icon: <item.icon size={22} strokeWidth={1.9} />,
      ...(count !== undefined && count > 0 ? { count } : {}),
    };
  };
  /**
   * Krok UXF4 bod 4 — v menu stoji CELY nazev cile („Nová zakázka"), samotne
   * „Nová" by neprozradilo, co se zaklada. Krok GRAF1 bod 5: na spodni liste
   * mobilu uz polozka NENI (lista je Přehled · Zakázky · Klienti · Kalendář ·
   * Menu), takze zkraceny popisek `shortLabel` uz nikdo nevykresluje.
   */
  const newCaseShellItem: AdminShellNavItem = {
    id: NEW_CASE_ITEM.id,
    label: NEW_CASE_ITEM.label,
    href: NEW_CASE_ITEM.path,
    active: isNewCasePath,
    icon: <NEW_CASE_ITEM.icon size={22} strokeWidth={1.9} />,
  };

  const topGroup: AdminShellNavGroup = {
    id: 'top',
    items: [toShellItem(navItemById('dnesek')), newCaseShellItem],
  };
  const groups: readonly AdminShellNavGroup[] = MENU_GROUP_ORDER.map((groupId) => ({
    id: groupId,
    label: MENU_GROUP_LABELS[groupId],
    items: visibleMenuItems.filter((item) => item.menuGroup === groupId).map(toShellItem),
  })).filter((group) => group.items.length > 0);
  const mainNav: readonly AdminShellNavGroup[] = [topGroup, ...groups];

  /*
   * Spodni lista mobilu — Přehled · Zakázky · Klienti · Kalendář a jako PATA
   * dlazdice „Menu" (krok GRAF1 bod 5; do GRAF1 byla tretí dlazdici „Nová").
   * „Menu" sem nedoplnuje `layout.tsx`, ale `AdminShell` — otevira jeho
   * vlastni panel, a kdyby ho dodavalo menu, musel by shell stav listy
   * vracet zpet nahoru.
   */
  const mobileNav: readonly AdminShellNavItem[] = MOBILE_BAR_IDS.map((id) =>
    toShellItem(navItemById(id)),
  );

  // Krok SML2 bod 1: editor smlouvy je rezim soustredeni — shell na nem
  // hlavni menu nevykresluje (predloha `Smlouva.dc.html` ho nema).
  const focusMode = isFocusModeRoute(pathname);

  return (
    <AdminShell
      focusMode={focusMode}
      productLogo={<ProductLogo testId="shell-logo" />}
      orgName={tenant?.name ?? null}
      homeHref="/prehled"
      mainNav={mainNav}
      mobileNav={mobileNav}
      routeKey={pathname}
      renderNavItem={(item, className, content, place) => (
        <Link
          to={item.href}
          className={className}
          aria-current={item.active ? 'page' : undefined}
          // Krok UXF4 bod 4: dlazdice nese zkraceny popisek, ctecka cely nazev.
          aria-label={item.ariaLabel}
          // Tataz polozka je v panelu i ve spodni liste mobilu — testid se
          // proto lisi podle mista, aby na strance nikdy nebyla dvakrat.
          data-testid={place === 'mobile' ? `mnav-${item.id}` : `menu-${item.id}`}
        >
          {content}
        </Link>
      )}
      search={{
        label: t('webAdmin.layout.searchLabel'),
        placeholder: t('webAdmin.caseTexts.searchPlaceholder'),
        shortcut: t('webAdmin.layout.searchShortcut'),
        onSearch: (query) => {
          void navigate({ to: '/zakazky', search: query.trim() !== '' ? { q: query.trim() } : {} });
        },
      }}
      notifications={{
        label: t('webAdmin.layout.bellLabel'),
        // Krok NOTIF1: k „ceka na vas" (smlouvy + faktury po splatnosti)
        // pribyvaji NEPRECTENA UPOZORNENI — zvonek je jedno misto, kde
        // makler vidi, ze na nej neco je.
        count: (contractsCount ?? 0) + overdueCount + (unreadNotifications.data?.unread ?? 0),
        // Vsechno, co „ceka na me", je na Dnesku — zvonek vede tam, kde to
        // uzivatel skutecne vyresi (drive mirl na stranku PRIPRAVUJE SE).
        onOpen: () => {
          void navigate({ to: '/prehled' });
        },
      }}
      userSlot={
        <>
          <CommandPalette />
          {user ? (
            <>
              {/*
                Krok UXD1 bod 11 (R.B. 12. 10. 22:21: „makléřka nevidí tlačítko
                na odhlášení", 22:22: „vpravo nahoře … hned vedle kolečka
                s iniciálou"). „Odhlásit" bylo jen uvnitr nabidky pod
                kolečkem — tedy na dve klepnuti a skryte.
                `userSlot` vykresluje shell na JEDNOM miste podle sirky
                (`admin-shell.tsx`): na mobilu v HORNI LISTE vpravo nahore,
                na desktopu v tmavem panelu u avataru (desktopova predloha
                v4 horni listu nema — krok UXR3). Tlacitko proto stoji vedle
                avataru a je videt z kazde obrazovky, na obou rozvrzenich.
                Odhlasuje TOUTEZ cestou (`handleLogout`) — zadna druha
                implementace.
              */}
              <button
                type="button"
                className="rh-a2-logout"
                onClick={() => void handleLogout()}
                data-testid="shell-logout"
              >
                <span className="rh-a2-ico" aria-hidden="true">
                  <LogOut size={18} strokeWidth={1.75} />
                </span>
                <span className="rh-a2-logout__label">{t('webAdmin.layout.logout')}</span>
              </button>
              <AvatarMenu
                name={user.name}
                email={user.email}
                onLogout={() => void handleLogout()}
                onReportProblem={openSupport}
              />
            </>
          ) : (
            <Link to="/login" className="rh-a2-link">
              {t('webAdmin.layout.login')}
            </Link>
          )}
        </>
      }
      onReportProblem={openSupport}
      texts={{
        skipLinkLabel: t('webAdmin.layout.skipLinkLabel'),
        navLabel: t('webAdmin.layout.navLabel'),
        moreMobileLabel: t('webAdmin.layout.moreMobileLabel'),
        closeLabel: t('webAdmin.layout.closeLabel'),
        reportProblemLabel: SUPPORT_TEXTS.openButton,
      }}
    >
      {/* Krok D1 bod 3: pruh „Ukázková data. Nic se neodesílá." je na KAZDE
          obrazovce administrace — demo se da otevrit odkudkoli. */}
      <DemoBanner />
      {/* Krok TRIAL1 bod 3: „konci zkusebni obdobi" patri na kazdou obrazovku —
          vyzva k platbe nesmi zalezet na tom, kde kancelar prave je. */}
      <TrialBanner />
      <Outlet />
      {/* Krok PRU1: zvyrazneni cile po „Ukaž mi" z karty pruvodce. */}
      <GuideHighlight />
      {/* Nahlasit problem — spoustene z avatar menu nebo paticky, aby neprekryvalo obsah (R.B. 1. 10., FIX4). */}
      <SupportWidget open={supportOpen} onOpenChange={setSupportOpen} />
    </AdminShell>
  );
}
